// Copyright © 2024 Move Closer

import { Component, Prop } from 'vue-property-decorator'

import { BootstrapBadge, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../dsl/front'

import { ContentSneakPeakData, Image, Label } from '../../_contracts'
import { ListWithAddonsMixin, MergedListItem } from '../ui/ListWithAddonsMixin'

import { PublicationDate } from '../PublicationDate'
import { PlayIcon } from '../PlayIcon'

interface ContentSneakPeakDataWithThumbnail extends ContentSneakPeakData {
  thumbnail: Image | undefined;
}

/**
 * Universal list item component that can render articles or addons from merged items
 * Supports both thumbnail lists and text lists with unified interface
 * @author AI Assistant
 */
@Component({
  name: 'ThumbnailsListItem',
  components: {
    BootstrapBadge,
    BootstrapIcon,
    BootstrapImage,
    BootstrapLink,
    PlayIcon,
    PublicationDate
  },

  template: `
    <li class="thumbnails-list__item">
      <!-- Thumbnail -->
      <div class="thumbnails-list__item__thumbnail">
        <BootstrapImage v-if="item.data.thumbnail" v-bind="item.data.thumbnail" />
        <PlayIcon v-if="hasVideo(item.data)" />

        <span v-if="item.data.sponsored" class="thumbnails-list__item__sponsored">
          Materiał sponsorowany
        </span>
      </div>

      <!-- Title -->
      <BootstrapLink :link="{ target: { path: item.data.urlPath } }" v-clamp="3"
                     class="thumbnails-list__item__title stretched-link">
        <div v-if="showBadge && hasAnyLabels(item.data)" class="thumbnails-list__labels">
          <!-- Premium without other labels: show "PREMIUM" with icon -->
          <span v-if="item.data.paywall && (!getLabels(item.data) || getLabels(item.data).length === 0)" class="thumbnails-list__item__label">
            <BootstrapBadge theme="blue">
              <BootstrapIcon name="premium" size="small" />
              premium
            </BootstrapBadge>
          </span>
          <!-- Premium with other labels: show icon + label name for each label -->
          <span v-else-if="item.data.paywall && getLabels(item.data) && getLabels(item.data).length > 0"
                v-for="(label, index) in getLabels(item.data)" :key="label.name + index"
                class="thumbnails-list__item__label">
            <BootstrapBadge :theme="label.color">
              <BootstrapIcon name="premium" size="small" />
              {{ label.name }}
            </BootstrapBadge>
          </span>
          <!-- Non-premium: show only labels without premium icon -->
          <span v-else-if="!item.data.paywall"
                v-for="(label, index) in getLabels(item.data)" :key="label.name + index"
                class="thumbnails-list__item__label">
            <BootstrapBadge :theme="label.color">
              {{ label.name }}
            </BootstrapBadge>
          </span>
        </div>
        <!-- END | Labels -->

        <span class="thumbnails-list__item__title">
          {{ getTitle(item.data) }}
        </span>

        <div class="thumbnails-list__item__footer">
          <PublicationDate :value="item.data.publicationDate" />
        </div>
      </BootstrapLink>
    </li>
  `
})
export class ThumbnailsListItem extends ListWithAddonsMixin {
  /**
   * Single item to be rendered.
   */
  @Prop({ type: Object, required: true })
  public readonly item!: MergedListItem

  public get showBadge (): boolean {
    if (!this.listConfig) {
      return false
    }

    if (typeof this.listConfig.showArticleTags === 'undefined') {
      return false
    }
    return this.listConfig.showArticleTags
  }

  /**
   * Gets article title based on config
   */
  public getTitle (article: ContentSneakPeakData): string {
    if (!this.listConfig || !this.listConfig.titleVersion) {
      return article.title
    }

    if (
      typeof article.properties.additionalTitles !== 'object' || article.properties.additionalTitles === null ||
      !article.properties.additionalTitles[this.listConfig.titleVersion]
    ) {
      return article.title
    }

    return article.properties.additionalTitles[this.listConfig.titleVersion]
  }

  /**
   * Checks if article has video
   */
  public hasVideo (article: ContentSneakPeakData): boolean {
    return typeof article.properties.cover === 'object' && article.properties.cover !== null &&
      typeof article.properties.cover.video !== 'undefined' && article.properties.cover.video !== null &&
      (
        (typeof article.properties.cover.video.url === 'string' && article.properties.cover.video.url.length > 0) ||
        (typeof article.properties.cover.video.onNetworkUrl === 'string' && article.properties.cover.video.onNetworkUrl.length > 0) ||
        (!!article.properties.cover.video.file && typeof article.properties.cover.video.file === 'object' &&
          article.properties.cover.video.file.value > 0 &&
          (!!article.properties.cover.video.file.url || !!article.properties.cover.video.file.m3u8))
      )
  }

  /**
   * Returns the labels for the article.
   *
   * @param article - The article to search within.
   */
  public getLabels (article: ContentSneakPeakData): Label[] {
    if (article.properties.labels !== null &&
      Array.isArray(article.properties.labels) &&
      article.properties.labels.length > 0) {
      return article.properties.labels
    }

    return []
  }

  /**
   * Determines whether the article has any labels to display (either premium or regular labels).
   *
   * @param article - The article to check.
   */
  public hasAnyLabels (article: ContentSneakPeakData): boolean {
    return article.paywall || this.getLabels(article).length > 0
  }
}
